import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tab, Nav, OverlayTrigger, Popover } from "react-bootstrap";
import { toast } from "react-toastify";
import { LoadingComponent, UserHover } from "Components";
import { NewRecommendation } from "./NewRecommendation";
import { RecommendationDetails } from "./RecommendationDetails";
import { SendReminder } from "./SendReminder";
import { newAuthService } from "Service/Axios";
import {
  getRecommendationListUrl,
  getRecommendationTabListUrl,
  UPDATE_MULTIPLE_TASK_DECISION_URL,
} from "Constant/api";
import { formatDateWithFullMonth, getFirstLetterOfName } from "Utils";

export default function Recommendation() {
  const { t } = useTranslation();
  const { menuId } = useParams();
  const navigate = useNavigate();

  const [requestLoading, setRequestLoading] = useState(false);
  const [givenLoading, setGivenLoading] = useState(false);
  const [recommendationModal, setRecommendationModal] = useState(false);
  const [newRecommendationModal, setNewRecommendationModal] = useState(false);
  const [sendReminderModal, setSendReminderModal] = useState(false);

  const [tabList, setTabList] = useState([]);
  const [requestList, setRequestList] = useState([]);
  const [givenList, setGivenList] = useState([]);
  const [activeTab, setActiveTab] = useState("givenRecommendation");

  const [recommendationDetails, setRecommendationDetails] = useState(null);

  // Toggle
  const toggleRecommendationModal = (recommendation) => {
    setRecommendationModal(!recommendationModal);
    if (recommendation) {
      setRecommendationDetails(recommendation);
    } else {
      setRecommendationDetails(null);
    }
  };

  const toggleNewRecommendationModal = (recommendation) => {
    setNewRecommendationModal(!newRecommendationModal);
    if (recommendation) {
      setRecommendationDetails(recommendation);
    } else {
      setRecommendationDetails(null);
    }
  };

  const toggleSendReminderModal = (recommendation) => {
    setSendReminderModal(!sendReminderModal);
    if (recommendation) {
      setRecommendationDetails(recommendation);
    } else {
      setRecommendationDetails(null);
    }
  };

  const toggleTab = (tab) => {
    setActiveTab(tab);
  };

  //  Listing
  const getTabList = async () => {
    const payload = { gid: menuId };
    const response = await newAuthService(
      getRecommendationTabListUrl,
      "POST",
      payload
    );
    if (response.status) {
      setTabList(response.data);
    } else {
      setTabList([]);
    }
  };

  useEffect(() => {
    getTabList();
  }, []);

  const getRequestList = async (tabId) => {
    setRequestLoading(true);
    const payload = { gid: tabId };
    const response = await newAuthService(
      getRecommendationListUrl,
      "POST",
      payload
    );
    if (response.status) {
      const modifiedResponse =
        response.data?.length > 0 &&
        response.data.map((data) => {
          const userDetails =
            data.fieldDetails.find(
              (fieldDetail) => fieldDetail.name === "Full Name"
            )?.userDetails?.[0] || null;
          return {
            ...data,
            userDetails,
          };
        });
      setRequestList(modifiedResponse);
      setRequestLoading(false);
    } else {
      setRequestList([]);
      setRequestLoading(false);
    }
  };

  const getGivenList = async (tabId) => {
    setGivenLoading(true);
    const payload = { gid: tabId };
    const response = await newAuthService(
      getRecommendationListUrl,
      "POST",
      payload
    );
    if (response.status) {
      const modifiedResponse =
        response.data?.length > 0 &&
        response.data.map((data) => {
          const userDetails =
            data.fieldDetails.find(
              (fieldDetail) => fieldDetail.name === "Full Name"
            )?.userDetails?.[0] || null;
          return {
            ...data,
            userDetails,
          };
        });
      setGivenList(modifiedResponse);
      setGivenLoading(false);
    } else {
      setGivenList([]);
      setGivenLoading(false);
    }
  };

  useEffect(() => {
    if (tabList?.length > 0) {
      getGivenList(tabList[0].gid);
    }
    if (tabList?.length > 1) {
      getRequestList(tabList[1].gid);
    }
  }, [tabList]);

  // Submit & Reset
  const onUpdateDecision = async (taskDecision) => {
    try {
      const payload = {
        gid: [recommendationDetails.gid],
        decision: taskDecision,
      };
      const response = await newAuthService(
        UPDATE_MULTIPLE_TASK_DECISION_URL,
        "POST",
        payload
      );
      if (response.status && response.data.success) {
        toast.success(t("common.messages.notification.success.updateStatus"));
        getGivenList(tabList[1].gid);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      if (error.response && error.response.data) {
        toast.error(error.response.data.message);
      }
    }
  };

  // Render
  const fieldDetailsRender = (fieldDetail) => {
    if (fieldDetail.designType === "date") {
      return (
        <span>
          {fieldDetail.name}:{" "}
          <span className="font-semibold">
            {fieldDetail.value && formatDateWithFullMonth(fieldDetail.value)}
          </span>
        </span>
      );
    }
    if (fieldDetail.designType === "status") {
      return (
        <span>
          Status:{" "}
          <span className="text-uppercase text-green-600">
            {fieldDetail.value}
          </span>
        </span>
      );
    }
    if (
      fieldDetail.designType === "files" ||
      fieldDetail.designType === "attachment"
    ) {
      return (
        <span>
          Supporting Document:{" "}
          {fieldDetail.dropDownData?.length > 0 ? (
            fieldDetail.dropDownData.map((item, itemIndex) => (
              <a
                key={itemIndex.toString()}
                href={item.value}
                className="font-semibold text-blue-700"
                target="_blank"
              >
                {item.name}
              </a>
            ))
          ) : (
            <span className="font-semibold">N/a</span>
          )}
        </span>
      );
    }
    return (
      <span>
        {fieldDetail.name}:{" "}
        <span className="font-semibold">{fieldDetail.value}</span>
      </span>
    );
  };

  return (
    <main className="main">
      <div className="rounded-3 bg-white mt-3">
        <Tab.Container
          // defaultActiveKey="requestedRecommendation"
          className="gray-tab p-3"
          activeKey={activeTab}
        >
          <Nav className="nav-tabs mb-3 gray-tab" role="tablist">
            <Nav.Item className="col" role="presentation">
              <button
                className={`nav-link d-block w-100 px-4 py-4 text-md ${
                  activeTab === "givenRecommendation" ? "active" : ""
                }`}
                onClick={() => toggleTab("givenRecommendation")}
              >
                <span className="material-symbols-outlined align-middle me-2">
                  border_color
                </span>
                Give a recommendation
              </button>
            </Nav.Item>
            <Nav.Item className="col" role="presentation">
              <button
                className={`nav-link d-block w-100 px-4 py-4 text-md ${
                  activeTab === "requestedRecommendation" ? "active" : ""
                }`}
                onClick={() => toggleTab("requestedRecommendation")}
              >
                <span className="material-symbols-outlined align-middle me-2">
                  quiz
                </span>
                Ask a recommendation
              </button>
            </Nav.Item>
          </Nav>
          <Tab.Content>
            {activeTab === "givenRecommendation" && (
              <Tab.Pane className="px-4 py-3 fade show active">
                <div className="d-flex align-items-center row-gap-3 mb-3">
                  <h3 className="h6 mb-0 me-3">Given Recommendations</h3>
                  {/* <ul className="filter-list ms-auto">
                    <li>
                      <button
                        className="bg-transparent border-0 py-2 px-4"
                        type="button"
                      >
                        <i className="pluto-filter"></i> Filters
                      </button>
                    </li>
                    <li>
                      <button
                        className="btn btn-secondary py-2 px-4"
                        type="button"
                        data-bs-toggle="modal"
                        data-bs-target="#newRecommendationRequestModal"
                        data-bs-whatever="@mdo"
                      >
                        Get new recommendation
                      </button>
                    </li>
                  </ul> */}
                </div>

                <div className="row gy-4">
                  <LoadingComponent isLoading={givenLoading}>
                    {givenList?.length > 0 &&
                      givenList.map((given, index) => (
                        <div
                          className="col-xxl-3 col-lg-4 col-sm-6 col-12"
                          key={`${given.gid}-${index}`}
                        >
                          <div
                            className="button bg-transparent border-0 h-100"
                            type="button"
                            onClick={() => toggleRecommendationModal(given)}
                          >
                            <div className="requested-box h-100 border-2 border rounded-2 bg-white overflow-hidden d-flex flex-column row-gap-3 pb-3 text-start">
                              <div className="px-3 pt-2 pb-3 border-bottom">
                                <div className="d-flex align-items-center row-gap-3 mb-3">
                                  <h4 className="text-sm mb-0 me-3 text-black-600">
                                    Requester
                                  </h4>
                                  <div className="ms-auto">
                                    <span className="material-symbols-outlined">
                                      pan_zoom
                                    </span>
                                  </div>
                                </div>
                                {given.userDetails && (
                                  <div className="hover-effect">
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={
                                        <Popover className="max-w-unset">
                                          <UserHover user={given} />
                                        </Popover>
                                      }
                                    >
                                      <div className="d-flex align-items-center">
                                        <div className="me-2 w-16 h-16 flex-none rounded-circle bg-grey-100 position-relative overflow-hidden">
                                          {given.userDetails?.photoURL ? (
                                            <img
                                              alt={`${given.userDetails?.firstName}-${given.userDetails?.lastName}`}
                                              className="w-100 h-100 object-fit-cover"
                                              src={given.userDetails?.photoURL}
                                            />
                                          ) : (
                                            getFirstLetterOfName(
                                              given.userDetails
                                            )
                                          )}
                                        </div>

                                        <div className="new-av-details">
                                          <h4 className="text-sm font-semibold">
                                            {given.userDetails?.firstName}{" "}
                                            {given.userDetails?.lastName}
                                          </h4>
                                          <ul className="m-0 p-0 list-unstyled text-xs d-flex flex-column row-gap-1">
                                            <li className="d-flex align-items-start text-break">
                                              <i className="bx bx-envelope mt-1 me-1"></i>{" "}
                                              {given.userDetails?.email}
                                            </li>
                                          </ul>
                                        </div>
                                      </div>
                                    </OverlayTrigger>
                                    {/* <div className="new-avater d-flex align-items-center">
                                  <div className="mb-2 position-relative">
                                    <div className="me-2 w-16 h-16 rounded-circle bg-grey-100 position-relative overflow-hidden">
                                      {given.userDetails?.photoURL ? (
                                        <img
                                          alt={`${given.userDetails?.firstName}-${given.userDetails?.lastName}`}
                                          className="w-100 h-100 object-fit-cover"
                                          src={given.userDetails?.photoURL}
                                        />
                                      ) : (
                                        getFirstLetterOfName(given.userDetails)
                                      )}
                                    </div>
                                  </div>
                                  <div className="new-av-details">
                                    <h4 className="text-sm font-semibold">
                                      {given.userDetails?.firstName}{" "}
                                      {given.userDetails?.lastName}
                                    </h4>
                                    <ul className="m-0 p-0 list-unstyled text-xs d-flex flex-column row-gap-1">
                                      <li className="d-flex align-items-center">
                                        <i className="bx bx-envelope me-1"></i>{" "}
                                        {given.userDetails?.email}
                                      </li>
                                    </ul>
                                  </div>
                                </div> */}
                                    {/* <UserHover user={given.userDetails} /> */}
                                  </div>
                                )}
                              </div>
                              <div className="px-3 recommender-show-content">
                                <ul className="m-0 mb-3 p-0 list-unstyled text-sm d-flex flex-column row-gap-1">
                                  {given.fieldDetails?.length > 0 &&
                                    given.fieldDetails
                                      .filter(
                                        (fieldDetail) =>
                                          fieldDetail.name !== "Full Name"
                                      )
                                      .map((fieldDetail, fieldIndex) => (
                                        <li
                                          className="py-1 d-flex align-items-center font-medium"
                                          key={fieldIndex.toString()}
                                        >
                                          {fieldDetailsRender(fieldDetail)}
                                        </li>
                                      ))}
                                </ul>
                              </div>
                              <div className="d-flex flex-wrap gap-2 mx-auto px-3 mt-auto">
                                {given.buttons?.length > 0 &&
                                  given.buttons.map((btn) => (
                                    <button
                                      key={btn.gid}
                                      className="btn py-2 px-4 col"
                                      style={{
                                        backgroundColor: btn.backgroundColor,
                                        color: btn.fontColor,
                                      }}
                                      onClick={() => {
                                        if (btn.type === "Form") {
                                          navigate(btn.url, {});
                                        } else {
                                          onUpdateDecision(btn.name);
                                        }
                                      }}
                                    >
                                      {btn.name}
                                    </button>
                                  ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </LoadingComponent>
                </div>

                {recommendationModal && (
                  <RecommendationDetails
                    show={recommendationModal}
                    recommendationDetails={recommendationDetails}
                    fieldDetailsRender={fieldDetailsRender}
                    toggle={toggleRecommendationModal}
                  />
                )}
              </Tab.Pane>
            )}

            {activeTab === "requestedRecommendation" && (
              <Tab.Pane className="px-4 py-3 fade show active">
                <div className="d-flex align-items-center row-gap-3 mb-3">
                  <h3 className="h6 mb-0 me-3">Requested Recommendations</h3>
                  <ul className="filter-list ms-auto">
                    <li>
                      <button
                        className="bg-transparent border-0 py-2 px-4"
                        type="button"
                      >
                        <i className="pluto-filter"></i> Filters
                      </button>
                    </li>
                    <li>
                      <button
                        className="btn btn-secondary py-2 px-4"
                        type="button"
                        onClick={toggleNewRecommendationModal}
                      >
                        Get new recommendation
                      </button>
                    </li>
                  </ul>
                </div>

                {newRecommendationModal && (
                  <NewRecommendation
                    show={newRecommendationModal}
                    recommendationDetails={recommendationDetails}
                    toggle={toggleNewRecommendationModal}
                  />
                )}

                <div className="row gy-4">
                  <LoadingComponent isLoading={requestLoading}>
                    {requestList?.length > 0 &&
                      requestList.map((request, index) => (
                        <div
                          className="col-xxl-3 col-lg-4 col-sm-6 col-12"
                          key={`${request.gid}-${index}`}
                        >
                          <div className="requested-box h-100 border-2 border success rounded-2 bg-white overflow-hidden position-relative">
                            <div className="p-3 border-bottom">
                              <div className="d-flex align-items-center row-gap-3 mb-3">
                                <h4 className="text-sm mb-0 me-3 text-black-600">
                                  Recommender
                                </h4>
                                {request.options?.length > 0 && (
                                  <div className="ms-auto">
                                    <button
                                      className="button bg-transparent border-0 d-flex align-items-center"
                                      type="button"
                                      data-bs-toggle="dropdown"
                                      aria-expanded="false"
                                    >
                                      <i className="bx bx-dots-vertical-rounded bx-sm"></i>
                                    </button>
                                    <ul className="dropdown-menu dropdown-menu-end border-0 shadow-sm">
                                      {request.options?.includes("Edit") && (
                                        <li>
                                          <button
                                            type="button"
                                            className="dropdown-item text-xs font-medium"
                                            onClick={() =>
                                              toggleNewRecommendationModal(
                                                request
                                              )
                                            }
                                          >
                                            Edit
                                          </button>
                                        </li>
                                      )}
                                      {request.options?.includes(
                                        "Copy Link"
                                      ) && (
                                        <li>
                                          <button
                                            type="button"
                                            className="dropdown-item text-xs font-medium"
                                          >
                                            Copy Link
                                          </button>
                                        </li>
                                      )}
                                      {request.options?.includes(
                                        "Send Reminder"
                                      ) && (
                                        <li>
                                          <button
                                            type="button"
                                            className="dropdown-item text-xs font-medium"
                                            onClick={() =>
                                              toggleSendReminderModal(request)
                                            }
                                          >
                                            Send Reminder
                                          </button>
                                        </li>
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                              {request.userDetails && (
                                <div>
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Popover className="max-w-unset">
                                        <UserHover user={request} />
                                      </Popover>
                                    }
                                  >
                                    <div className="d-flex align-items-center">
                                      <div className="me-2 w-16 h-16 flex-none rounded-circle bg-grey-100 position-relative overflow-hidden">
                                        {request.userDetails?.photoURL ? (
                                          <img
                                            alt={`${request.userDetails?.firstName}-${request.userDetails?.lastName}`}
                                            className="w-100 h-100 object-fit-cover"
                                            src={request.userDetails?.photoURL}
                                          />
                                        ) : (
                                          getFirstLetterOfName(
                                            request.userDetails
                                          )
                                        )}
                                      </div>

                                      <div className="new-av-details">
                                        <h4 className="text-sm font-semibold">
                                          {request.userDetails?.firstName}{" "}
                                          {request.userDetails?.lastName}
                                        </h4>
                                        <ul className="m-0 p-0 list-unstyled text-xs d-flex flex-column row-gap-1">
                                          <li className="d-flex align-items-start text-break">
                                            <i className="bx bx-envelope mt-1 me-1"></i>{" "}
                                            {request.userDetails?.email}
                                          </li>
                                        </ul>
                                      </div>
                                    </div>
                                  </OverlayTrigger>
                                  {/* <div className="hover-effect">
                                  <div className="new-avater d-flex align-items-center">
                                    <div className="mb-2 position-relative">
                                      <div className="me-2 w-16 h-16 rounded-circle bg-grey-100 position-relative overflow-hidden">
                                        {request.userDetails?.photoURL ? (
                                          <img
                                            alt={`${request.userDetails?.firstName}-${request.userDetails?.lastName}`}
                                            className="w-100 h-100 object-fit-cover"
                                            src={request.userDetails?.photoURL}
                                          />
                                        ) : (
                                          getFirstLetterOfName(
                                            request.userDetails
                                          )
                                        )}
                                      </div>
                                    </div>
                                    <div className="new-av-details">
                                      <h4 className="text-sm font-semibold">
                                        {request.userDetails?.firstName}{" "}
                                        {request.userDetails?.lastName}
                                      </h4>
                                      <ul className="m-0 p-0 list-unstyled text-xs d-flex flex-column row-gap-1">
                                        <li className="d-flex align-items-center">
                                          <i className="bx bx-envelope me-1"></i>{" "}
                                          {request.userDetails?.email}
                                        </li>
                                      </ul>
                                    </div>
                                  </div>
                                  <UserHover user={request.userDetails} />
                                </div> */}
                                  {/* <UserHover user={request.userDetails} /> */}
                                </div>
                              )}
                            </div>
                            <div className="p-3">
                              <ul className="m-0 p-0 list-unstyled text-sm d-flex flex-column row-gap-1">
                                {request.fieldDetails?.length > 0 &&
                                  request.fieldDetails
                                    .filter(
                                      (fieldDetail) =>
                                        fieldDetail.name !== "Full Name"
                                    )
                                    .map((fieldDetail, fieldIndex) => (
                                      <li
                                        className="py-1 d-flex align-items-center font-medium text-break"
                                        key={fieldIndex.toString()}
                                      >
                                        {fieldDetailsRender(fieldDetail)}
                                      </li>
                                    ))}
                              </ul>
                            </div>
                            <div className="status-line position-absolute start-0 bottom-0 end-0"></div>
                          </div>
                        </div>
                      ))}
                  </LoadingComponent>
                </div>

                {sendReminderModal && (
                  <SendReminder
                    show={sendReminderModal}
                    recommendationDetails={recommendationDetails}
                    toggle={toggleSendReminderModal}
                  />
                )}
              </Tab.Pane>
            )}
          </Tab.Content>
        </Tab.Container>
      </div>
    </main>
  );
}
